import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, UserPlus, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtFecha = (f) => (f ? f.split('-').reverse().join('/') : '');

function InscriptoRow({ i, creando, onCrear }) {
  const nombre = `${i.apellido || ''}${i.apellido && i.nombre ? ', ' : ''}${i.nombre || ''}`.trim() || i.email || '—';
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-b last:border-b-0" data-testid={`sello-manual-row-${i.inscripcion_id}`}>
      <div className="min-w-0">
        <p className="font-medium text-sm text-slate-800 truncate">{nombre}</p>
        <p className="text-xs text-slate-500 truncate">
          {i.matricula ? `MP ${i.matricula}` : 'Sin matrícula'}{i.dni ? ` · DNI ${i.dni}` : ''}{i.email ? ` · ${i.email}` : ''}
        </p>
      </div>
      {i.pedido_id ? (
        <Badge className="bg-green-100 text-green-800 shrink-0" data-testid={`sello-manual-ya-tiene-${i.inscripcion_id}`}>
          <CheckCircle2 className="w-3 h-3 mr-1" /> Ya tiene pedido
        </Badge>
      ) : (
        <Button size="sm" onClick={() => onCrear(i)} disabled={!!creando} className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
          data-testid={`sello-manual-crear-${i.inscripcion_id}`}>
          {creando === i.inscripcion_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
          Crear pedido
        </Button>
      )}
    </div>
  );
}

export function SelloCargaManualDialog({ open, onOpenChange, token, onCreado }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState('');
  const [inscriptos, setInscriptos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [creando, setCreando] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/admin/sellos/eventos-elegibles`, { headers })
      .then(r => setEventos(r.data.eventos || []))
      .catch(() => toast.error('No se pudieron cargar los eventos'));
  }, [open, headers]);

  useEffect(() => {
    if (!eventoId) { setInscriptos([]); return; }
    setLoading(true);
    axios.get(`${API_URL}/admin/sellos/eventos/${eventoId}/inscriptos`, { headers })
      .then(r => setInscriptos(r.data.inscriptos || []))
      .catch(err => toast.error(err.response?.data?.detail || 'No se pudieron cargar los inscriptos'))
      .finally(() => setLoading(false));
  }, [eventoId, headers]);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return inscriptos;
    return inscriptos.filter(i => [i.nombre, i.apellido, i.dni, i.matricula, i.email]
      .some(v => (v || '').toString().toLowerCase().includes(t)));
  }, [inscriptos, q]);

  const crear = async (i) => {
    const nombre = `${i.nombre || ''} ${i.apellido || ''}`.trim() || i.email;
    if (!window.confirm(`¿Crear el pedido de sello para ${nombre}? Se le enviará el QR por email.`)) return;
    setCreando(i.inscripcion_id);
    try {
      const r = await axios.post(`${API_URL}/admin/sellos/pedidos/manual`,
        { evento_id: eventoId, inscripcion_id: i.inscripcion_id }, { headers });
      const ped = r.data.pedido;
      setInscriptos(prev => prev.map(x => x.inscripcion_id === i.inscripcion_id
        ? { ...x, pedido_id: ped.id, pedido_estado: ped.estado } : x));
      toast.success(`Pedido creado para ${nombre}. Quedó en "Solicitados"${ped.email ? ' y se envió el QR por email' : ''}.`);
      onCreado?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear el pedido');
    }
    setCreando(null);
  };

  const pendientes = inscriptos.filter(i => !i.pedido_id).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1.5rem)] sm:w-full" data-testid="sello-manual-dialog">
        <DialogHeader>
          <DialogTitle>Cargar pedido de sello manualmente</DialogTitle>
          <DialogDescription>
            Para inscriptes confirmades que no pudieron escanear el QR o solicitarlo. Solo eventos con beneficio de sello de hoy o pasados.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <select className="w-full min-w-0 border rounded-md px-3 py-2 text-sm bg-white" value={eventoId}
            onChange={e => { setEventoId(e.target.value); setQ(''); }} data-testid="sello-manual-evento">
            <option value="">{eventos.length ? 'Elegí un evento…' : 'No hay eventos con beneficio de sello de hoy o pasados'}</option>
            {eventos.map(ev => <option key={ev.id} value={ev.id}>{fmtFecha(ev.fecha)} · {ev.nombre}</option>)}
          </select>
          {eventoId && (
            <>
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, DNI, matrícula o email…"
                  className="h-9" data-testid="sello-manual-buscar" />
              </div>
              <p className="text-xs text-slate-500" data-testid="sello-manual-resumen">
                {inscriptos.length} inscripte(s) confirmade(s) · {pendientes} sin pedido
              </p>
              <div className="border rounded-md max-h-[50vh] overflow-y-auto">
                {loading ? (
                  <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-purple-600" /></div>
                ) : filtrados.length === 0 ? (
                  <p className="p-6 text-center text-sm text-slate-500">Sin inscriptes que coincidan</p>
                ) : filtrados.map(i => <InscriptoRow key={i.inscripcion_id} i={i} creando={creando} onCrear={crear} />)}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
